import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DiagnosticIdentityProvider, DiagnosticView } from '@shared/prospect-diagnostic';
import { normalizeWebsiteDomain } from '@shared/company-signup';
import { cleanDiagnosticSource } from '@shared/prospect-diagnostic';
import { getTurnstileToken } from '@/lib/turnstile';
import { DiagnosticAlert, DiagnosticLayout, StepNav, diagnosticPrimary } from '@/components/diagnostic/DiagnosticParts';
import { DiagnosticQuestion } from '@/components/diagnostic/DiagnosticQuestion';
import { DiagnosticIdentity } from '@/components/diagnostic/DiagnosticIdentity';
import { DiagnosticReady } from '@/components/diagnostic/DiagnosticReady';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { type DiagnosticDraft, type DraftPatch, diagnosticSteps, emptyDraft, isStepComplete, toAnswers } from '@/lib/diagnostic-steps';
import {
  alreadyAnnounced, announceLeadStart, clearDraft, emailLeadDiagnostic, markAnnounced, generateLeadDiagnostic, loadDraft, loadToken, readLeadConfig, readLeadDiagnostic, saveDraft, saveToken,
} from '@/lib/lead-diagnostic';

type Phase = 'loading' | 'questions' | 'identity' | 'ready';

/** How long a chosen card stays on screen, checked, before the next question. */
const ANSWER_BEAT_MS = 280;

/** How often the page rereads a diagnostic being written (a read, no AI). */
const POLL_MS = 4000;

/**
 * Online diagnostic (lead magnet, P1, 28/10/2026): public, outside the app shell and without a
 * Domino account. Questions one per screen, then the identity check, then the diagnostic.
 * `?site=acme.fr` (outbound link) fills the first answer.
 */
export const DiagnosticPage = () => {
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('loading');
  const [draft, setDraft] = useState<DiagnosticDraft>(() => {
    const saved = loadDraft();
    const site = searchParams.get('site');
    // Where the visitor came from (`?source=`, else `?utm_source=`), kept for Benoit's alerts.
    const source = cleanDiagnosticSource(searchParams.get('source') ?? searchParams.get('utm_source'));
    const draft = saved ?? { ...emptyDraft(), website: site ?? '' };
    return source ? { ...draft, source } : draft;
  });
  const turnstileRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [providers, setProviders] = useState<DiagnosticIdentityProvider[]>([]);
  const [diagnostic, setDiagnostic] = useState<DiagnosticView | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');

  useEffect(() => {
    void readLeadConfig().then((config) => { if (config.ok) setProviders(config.providers); });
    const saved = loadToken();
    if (!saved) { setPhase('questions'); return; }
    void readLeadDiagnostic(saved).then((result) => {
      if (result.ok) { setToken(saved); setDiagnostic(result.diagnostic); setPhase('ready'); return; }
      // Only a diagnostic the server no longer knows is forgotten; a network blip keeps it.
      if (result.code === 'unknown') { saveToken(null); setPhase('questions'); return; }
      setLoadError(result.message);
    });
  }, []);

  const writing = diagnostic?.status === 'pending' || diagnostic?.status === 'generating';
  useEffect(() => {
    if (!token || !writing) return;
    const timer = window.setInterval(() => {
      void readLeadDiagnostic(token).then((result) => { if (result.ok) setDiagnostic(result.diagnostic); });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [token, writing]);

  const emailDiagnostic = (current: string | null) =>
    current ? emailLeadDiagnostic(current) : Promise.resolve({ ok: false as const, code: 'unknown', message: 'Rechargez la page.' });

  const retry = () => {
    if (!token) return;
    setRetrying(true);
    void generateLeadDiagnostic(token).then((result) => {
      setRetrying(false);
      if (result.ok) setDiagnostic(result.diagnostic);
    });
  };

  const update = (patch: DraftPatch) => {
    setDraft((current) => {
      const next = { ...current, ...(typeof patch === 'function' ? patch(current) : patch) };
      saveDraft(next);
      return next;
    });
  };

  const steps = diagnosticSteps(draft);
  const step = steps[Math.min(index, steps.length - 1)] ?? 'website';
  const websiteValid = normalizeWebsiteDomain(draft.website) !== null;
  const answers = toAnswers(draft);
  const total = steps.length + 1;

  // 03/11: Benoit hears of each visitor who answers the first question (to drop if too many).
  const announceStart = () => {
    const domain = normalizeWebsiteDomain(draft.website);
    if (!domain || alreadyAnnounced(domain) || !turnstileRef.current) return;
    markAnnounced(domain);
    void getTurnstileToken(turnstileRef.current, 'diagnostic_start')
      .then((token) => announceLeadStart(domain, draft.source, token))
      .catch((error: unknown) => console.warn('[diagnostic] start alert not sent', error));
  };

  const next = () => {
    if (step === 'website') announceStart();
    setDirection('forward');
    if (index >= steps.length - 1) setPhase('identity');
    else setIndex(index + 1);
  };

  const back = () => {
    setDirection('back');
    if (phase === 'identity') setPhase('questions');
    else setIndex(index - 1);
  };

  // A single choice moves on by itself, after a beat so the chosen card is seen checked.
  const advancing = useRef(false);
  const answered = () => {
    if (advancing.current) return;
    advancing.current = true;
    window.setTimeout(() => { advancing.current = false; next(); }, ANSWER_BEAT_MS);
  };

  // Each question comes in from the side it is reached from: the change is seen even
  // when two questions look alike (Benoit, 29/10).
  const enter = cn('flex flex-col gap-8', direction === 'forward' ? 'motion-safe:animate-questionIn' : 'motion-safe:animate-questionBack', 'motion-reduce:animate-fadeIn');

  const opened = (newToken: string, view: DiagnosticView) => {
    saveToken(newToken);
    setToken(newToken);
    clearDraft();
    setDiagnostic(view);
    setPhase('ready');
  };

  if (phase === 'loading' && loadError) {
    return (
      <DiagnosticLayout progress={null}>
        <DiagnosticAlert>{loadError}</DiagnosticAlert>
        <button type="button" onClick={() => window.location.reload()} className={`${diagnosticPrimary} self-start`}>Réessayer</button>
      </DiagnosticLayout>
    );
  }

  if (phase === 'loading') {
    return (
      <DiagnosticLayout progress={null}>
        <SkeletonGroup label="Chargement" className="flex flex-col gap-6">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </SkeletonGroup>
      </DiagnosticLayout>
    );
  }

  if (phase === 'ready' && diagnostic) {
    return <DiagnosticLayout progress={null}><DiagnosticReady diagnostic={diagnostic} onRetry={retry} retrying={retrying} onEmail={() => emailDiagnostic(token)} /></DiagnosticLayout>;
  }

  if (phase === 'identity' && answers) {
    return (
      <DiagnosticLayout progress={(total - 1) / total}>
        <div key="identity" className={enter}>
          <DiagnosticIdentity answers={answers} providers={providers} onBack={back} onOpened={opened} />
        </div>
      </DiagnosticLayout>
    );
  }

  return (
    <DiagnosticLayout progress={index / total}>
      <div key={step} className={enter}>
        <DiagnosticQuestion step={step} draft={draft} onChange={update} onAnswered={answered} />
        <StepNav
          onBack={index > 0 ? back : null}
          onNext={next}
          nextLabel={index >= steps.length - 1 ? 'Découvrir le potentiel' : 'Continuer'}
          nextDisabled={!isStepComplete(step, draft, websiteValid)}
        />
      </div>
      {/* Outside the question: the start check may answer after the next question shows. */}
      <div ref={turnstileRef} />
    </DiagnosticLayout>
  );
};
